"use client";

import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { buildTracks, type Point } from "./meeting-timeline";

// Plays the Meetings demo like a video. Every animated element gets one Web Animation over the loop, all driven
// by one clock, so a pause freezes the frame and play resumes from it.
//   Mouse: poster until the pointer is over the screen, pause when it leaves.
//   Touch or no hover: plays while half the screen is visible.
//   Reduced motion: no animation at all, the markup's still frame stays.
// Paused whenever the tab is hidden or the screen is off-screen.

type DemoPlayerProps = {
  label: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
};

/** Centre of an element in stage pixels, from layout offsets (transforms and the stage's scale ignored). */
function centreIn(stage: HTMLElement, el: HTMLElement | null, maxHalfWidth = Infinity): Point {
  if (!el) return { x: 0, y: 0 };
  let x = 0;
  let y = 0;
  let node: HTMLElement | null = el;
  while (node && node !== stage) {
    x += node.offsetLeft;
    y += node.offsetTop;
    const parent = node.offsetParent as HTMLElement | null;
    if (parent && parent !== stage) {
      x += parent.clientLeft;
      y += parent.clientTop;
    }
    node = parent;
  }
  return { x: x + Math.min(el.offsetWidth / 2, maxHalfWidth), y: y + el.offsetHeight / 2 };
}

/**
 * Calls `onChange` once things settle after the stage's layout may have moved: web fonts finished loading (Suisse
 * arrives with display: swap and reflows the text) or the stage changed size (the phone layout below 720 px).
 * Nothing runs per frame. Returns a stop function.
 */
export function watchLayout(stage: HTMLElement, onChange: () => void, settleMs = 150): () => void {
  let timer: number | undefined;
  let stopped = false;
  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(onChange, settleMs);
  };
  const resize = "ResizeObserver" in window ? new ResizeObserver(schedule) : null;
  resize?.observe(stage);
  document.fonts.addEventListener("loadingdone", schedule);
  void document.fonts.ready.then(() => {
    if (!stopped) schedule();
  });
  return () => {
    stopped = true;
    window.clearTimeout(timer);
    resize?.disconnect();
    document.fonts.removeEventListener("loadingdone", schedule);
  };
}

export default function DemoPlayer({ label, className, style, children }: DemoPlayerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    const stage = root?.querySelector<HTMLElement>("[data-demo-stage]");
    if (!root || !stage || typeof stage.animate !== "function") return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mouse = window.matchMedia("(hover: hover) and (pointer: fine)");
    let animations: Animation[] = [];
    let clock = 0; // ms played so far, shared by every animation
    let ratio = 0; // visible share of the screen
    let hovering = false;
    let aimed = ""; // the layout the tracks were built for

    const readClock = () => {
      const time = animations[0]?.currentTime;
      if (time != null) clock = Number(time);
    };

    const measure = () => {
      const target = (key: string) => stage.querySelector<HTMLElement>(`[data-demo-target="${key}"]`);
      // How far the list moves up without the new meeting: its row's height plus the gap between rows. Below 720
      // the rows are no longer all the same height, so this number — and not each row's own height — is what the
      // past rows must travel (see buildTracks).
      const rows = stage.querySelector<HTMLElement>(".v2-os__rows");
      const fresh = stage.querySelector<HTMLElement>('[data-demo="row-new"]');
      const gap = rows ? parseFloat(getComputedStyle(rows).rowGap) || 0 : 0;
      return {
        targets: {
          record: centreIn(stage, target("record")),
          stop: centreIn(stage, target("stop")),
          row: centreIn(stage, target("row"), 140),
          check: centreIn(stage, target("check")),
        },
        size: { width: stage.offsetWidth, height: stage.offsetHeight },
        shift: fresh ? fresh.offsetHeight + gap : undefined,
      };
    };

    const build = (layout = measure()) => {
      readClock();
      animations.forEach((animation) => animation.cancel());
      aimed = JSON.stringify(layout);
      const tracks = buildTracks(layout.targets, layout.size, layout.shift);
      animations = tracks.flatMap(({ key, keyframes, duration }) =>
        [...stage.querySelectorAll<HTMLElement | SVGElement>(`[data-demo="${key}"]`)].map((el) =>
          el.animate(keyframes, { duration, iterations: Infinity, fill: "both" }),
        ),
      );
      // New animations start at 0 and running: park them on the shared clock until sync() decides.
      for (const animation of animations) {
        animation.currentTime = clock;
        animation.pause();
      }
    };

    const sync = () => {
      if (reduce.matches) {
        animations.forEach((animation) => animation.cancel());
        animations = [];
        root.dataset.mode = "still";
        root.dataset.playing = "false";
        return;
      }
      if (animations.length === 0) build();
      readClock();
      const playing = !document.hidden && (mouse.matches ? hovering && ratio > 0 : ratio >= 0.5);
      for (const animation of animations) {
        animation.currentTime = clock;
        if (playing) animation.play();
        else animation.pause();
      }
      root.dataset.mode = mouse.matches ? "hover" : "auto";
      root.dataset.playing = String(playing);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        ratio = entry?.intersectionRatio ?? 0;
        root.dataset.visible = String(ratio > 0);
        sync();
      },
      { threshold: [0, 0.5] },
    );
    io.observe(root);

    const onEnter = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      hovering = true;
      sync();
    };
    const onLeave = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      hovering = false;
      sync();
    };

    // Cursor targets move when the layout changes after mount (fonts, phone layout): if one did, rebuild on the same clock.
    const stopWatching = watchLayout(stage, () => {
      if (reduce.matches) return;
      const layout = measure();
      if (JSON.stringify(layout) === aimed) return;
      build(layout);
      sync();
    });

    root.addEventListener("pointerenter", onEnter);
    root.addEventListener("pointerleave", onLeave);
    document.addEventListener("visibilitychange", sync);
    reduce.addEventListener("change", sync);
    mouse.addEventListener("change", sync);
    sync();

    return () => {
      io.disconnect();
      stopWatching();
      root.removeEventListener("pointerenter", onEnter);
      root.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("visibilitychange", sync);
      reduce.removeEventListener("change", sync);
      mouse.removeEventListener("change", sync);
      animations.forEach((animation) => animation.cancel());
    };
  }, []);

  return (
    <div ref={ref} className={className} style={style} role="img" aria-label={label} data-mode="still">
      {children}
    </div>
  );
}
